import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as tj from '@mapbox/togeojson';
import { Store } from '@ngrx/store';
import { FeatureCollection } from 'geojson';
import * as JSZip from 'jszip';
import { parse } from 'papaparse';
import { Observable } from 'rxjs';
import { finalize, map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { Parcel, Region, YieldCurve } from '../../shared/models';
import { deserialize, serialize } from '../../shared/models/base-helper';
import { SpinnerService } from '../../shared/spinner/spinner.service';
import * as fromApp from '../../store';
import { addRegionKMLContent, addYieldCurveContent } from '../../store/admin/admin.actions';

export interface RegionService {
  jsonb_build_object: any;
}

@Injectable({
  providedIn: 'root',
})
export class RegionService {
  apiUrl = environment.apiUrl + '/regions/';

  constructor(
    readonly http: HttpClient,
    readonly store: Store<fromApp.AppState>,
    readonly spinnerService: SpinnerService
  ) {}

  async parseKmlDocument(file) {
    const fileReader = new FileReader();
    fileReader.onload = async (e: any) => {
      this.extractAndParseKML(file.name, e.target.result);
    };
    fileReader.readAsText(file);
  }

  async parseKmzDocument(file) {
    const fileReader = new FileReader();
    fileReader.onload = async (e: any) => {
      await this.extractAndParseKMZ(file.name, e.target.result);
    };
    fileReader.readAsArrayBuffer(file);
  }

  extractAndParseKML(fileName: string, fileData) {
    const geoJSONFromKML: FeatureCollection = this.parseKMLContent(fileData);
    const geoJSONGeometry = geoJSONFromKML.features[0].geometry;
    this.store.dispatch(addRegionKMLContent({ data: { fileName, kmlGeoJSON: geoJSONGeometry } }));
  }

  extractAndParseKMZ(fileName: string, fileData) {
    const zipParser = new JSZip();
    return zipParser.loadAsync(fileData).then(
      zip => {
        zip.forEach((relativePath, zipEntry) => {
          zipEntry.async('text').then(content => {
            const geoJSONFromKML: FeatureCollection = this.parseKMLContent(content);
            if (geoJSONFromKML.features?.[0]) {
              const geoJSONGeometry = geoJSONFromKML.features[0].geometry;
              this.store.dispatch(
                addRegionKMLContent({
                  data: { fileName, kmlGeoJSON: geoJSONGeometry },
                })
              );
            }
          });
        });
      },
      e => {
        throw new Error('error converting the =' + e.message);
      }
    );
  }

  parseKMLContent = (content: string): FeatureCollection => {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(content, 'text/xml');
    return tj.kml(xmlDoc);
  };

  createRegion(regionName: string, regionRefreshDate: string, kmlGeometry: string, yieldCurveRows: Array<YieldCurve>) {
    const newRegion = new Region({
      name: regionName,
      startYear: 2020,
      startQuarter: 3,
      activeDate: Date.now(),
      inactiveDate: Date.now(),
      boundary: kmlGeometry,
      yieldCurves: yieldCurveRows,
      refreshDate: regionRefreshDate,
    });

    return this.http.post(this.apiUrl, serialize(newRegion));
  }

  getIncluded(parcels: Parcel[]) {
    this.spinnerService.show('app-spinner');
    return this.http.post<Array<any>>(`${this.apiUrl}included`, serialize(parcels)).pipe(
      map(parcels => parcels.map(p => p.ll_uuid)),
      finalize(() => this.spinnerService.hide('app-spinner'))
    );
  }

  getRegionsByLatLong(lat: number = null, lng: number = null): Observable<Region[]> {
    let params = new HttpParams();

    if (lat && lng) {
      params = params.append('lat', lat.toString());
      params = params.append('lng', lng.toString());
    }

    return this.http
      .get<any[]>(`${this.apiUrl}lat-long`, { params })
      .pipe(map(regions => regions.map((serializedRegion: any) => deserialize(serializedRegion, Region))));
  }

  getAllRegions() {
    return this.http
      .get<any[]>(this.apiUrl)
      .pipe(map(regions => regions.map((serializedRegion: any) => deserialize(serializedRegion, Region))));
  }

  getRegion(id: number): Observable<Region> {
    return this.http
      .get<any>(`${this.apiUrl}${id}`)
      .pipe(map(serializedRegion => deserialize(serializedRegion, Region)));
  }

  async parseCsvDocument(file) {
    const fileReader = new FileReader();
    fileReader.onload = async () => {
      parse(file, {
        complete: result => {
          if (!result.data || result.data[0][0] !== 'STRATA' || result.data.length < 2) {
            throw new Error('Yield Curve file is not the proper format');
          } else {
            const colCount = result.data[0].length;
            const strataYieldCurve: Array<YieldCurve> = [];
            for (let index = 1; index < result.data.length; index++) {
              // don't add lines that have no content
              if (result.data[index] && colCount === result.data[index].length) {
                const row: YieldCurve = new YieldCurve({
                  strata: result.data[index][0],
                  yearArray: result.data[index].slice(1, result.data[index].length).join(','),
                });
                strataYieldCurve.push(row);
              }
            }
            this.store.dispatch(
              addYieldCurveContent({
                data: {
                  fileName: file.name,
                  yieldCurveData: strataYieldCurve,
                },
              })
            );
          }
        },
      });
    };
    fileReader.readAsText(file);
  }

  updateRegion(region): Observable<Region> {
    return this.http
      .put<any>(`${this.apiUrl}${region.id}`, serialize(region))
      .pipe(map(serializedRegion => deserialize(serializedRegion, Region)));
  }
}
